<template>
  <transition
    enter-active-class="transition duration-150 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-100 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="show"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="dismiss"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg w-full max-w-lg shadow-xl overflow-hidden">
        <div class="bg-gradient-to-r from-primary to-primary-light px-6 py-5">
          <h2 class="text-lg font-semibold text-white">Set up your trust center automatically</h2>
          <p class="text-sm text-white text-opacity-85 mt-1">Takes a few minutes, no manual data entry</p>
        </div>

        <div class="px-6 py-5">
          <p class="text-gray-600 dark:text-gray-300 mb-4">
            AI analysis reads {{ tenantName ? tenantName + "'s" : 'your' }} public website and
            pre-fills your compliance frameworks, security categories and policy content so you
            start from a populated trust center instead of a blank one.
          </p>

          <ul class="text-sm text-gray-600 dark:text-gray-300 space-y-2 mb-5">
            <li class="flex items-start">
              <span class="text-primary dark:text-primary-light mr-2">&bull;</span>
              Detects which frameworks apply, such as CRA, NIS2 and GDPR
            </li>
            <li class="flex items-start">
              <span class="text-primary dark:text-primary-light mr-2">&bull;</span>
              Drafts your security category content from published policies
            </li>
            <li class="flex items-start">
              <span class="text-primary dark:text-primary-light mr-2">&bull;</span>
              You review and edit everything before it goes live
            </li>
          </ul>

          <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              @click="dismiss"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Maybe later
            </button>
            <button
              @click="start"
              class="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-md transition-colors"
            >
              Start AI analysis
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

// Dismissal is keyed per tenant: an admin managing several tenants should be
// prompted once for each, not once ever. Bumping the version suffix re-prompts
// everyone, which is the escape hatch if the copy or feature changes.
const DISMISS_KEY = (tenantId) => `onboardingPromptDismissed_v1_${tenantId}`

export default {
  name: 'OnboardingPrompt',
  props: {
    tenantId: { type: String, default: null },
    tenantName: { type: String, default: '' }
  },
  setup (props) {
    const store = useStore()
    const router = useRouter()
    const show = ref(false)

    const dismiss = () => {
      show.value = false
      if (props.tenantId) {
        localStorage.setItem(DISMISS_KEY(props.tenantId), 'true')
      }
    }

    const start = () => {
      dismiss()
      router.push(`/tenants/${props.tenantId}/settings`)
    }

    const maybePrompt = async (tenantId) => {
      show.value = false
      if (!tenantId || localStorage.getItem(DISMISS_KEY(tenantId))) return

      try {
        const data = await store.dispatch('fetchTenantOnboarding', tenantId)
        // Don't interrupt a run that's already going, and never nag a tenant
        // that has already been onboarded.
        if (!data.has_onboarded && !data.in_progress) {
          show.value = true
        }
      } catch (e) {
        // A failed check must stay silent. Showing the prompt on error would
        // nag tenants who are already onboarded whenever the API hiccups.
      }
    }

    watch(() => props.tenantId, maybePrompt, { immediate: true })

    return { show, dismiss, start }
  }
}
</script>
